import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {createPromoCode, updatePromoCode, fetchPromoCodeById} from '../../services/promocode-service.js';
import {useLanguage} from '../../utils/languageContext.js';
import {getTranslation} from '../../utils/translate.js';
import Toggle from '../comman/toggle.js';
import Button from '../comman/button.js';
import LoadingSpinner from '../LoadingSpinner/loadingSpinner.js';
import SingleDatePicker from '../comman/singleDatePicker.js';

const PREFILLABLE_FIELDS = ['code', 'type', 'value', 'upperLimitValue', 'validFrom', 'validTo', 'minimumOrderAmount', 'maxUses', 'maxUsesPerUser', 'userPhoneNumber'];
const PREFILLABLE_TYPES = ['FIXED', 'PERCENTAGE', 'DELIVERY_PERCENTAGE'];

const prefillFromSearchParams = (searchParams) => {
    const prefill = {};
    PREFILLABLE_FIELDS.forEach((field) => {
        const value = searchParams.get(field);
        if (value === null || value === '') return;
        if (field === 'type' && !PREFILLABLE_TYPES.includes(value)) return;
        prefill[field] = value;
    });
    // The phone field lives inside the customer-limit section; open it so the target is visible
    if (prefill.userPhoneNumber) {
        prefill.enableCustomerLimit = true;
    }
    return prefill;
};

const PromocodeForm = ({mode = 'create', promocodeId, onCancel, onSuccess, toastRef}) => {
    const {language} = useLanguage();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const isReadOnly = false; // Always allow editing
    const [searchParams] = useSearchParams();

    // Form options
    const typeOptions = [
        {value: 'FIXED', label: getTranslation('promocodes.types.FIXED', language)},
        {value: 'PERCENTAGE', label: getTranslation('promocodes.types.PERCENTAGE', language)},
        {value: 'DELIVERY_PERCENTAGE', label: getTranslation('promocodes.types.DELIVERY_PERCENTAGE', language)}
    ];

    const paymentMethodOptions = useMemo(() => [
        {value: ['CASH', 'CARD', 'CARD_ON_DELIVERY', 'TELCELL', 'IDRAM', 'FASTSHIFT', 'PAYLINK'], label: getTranslation('promocodes.paymentMethods.any', language)},
        {value: ['CARD', 'CARD_ON_DELIVERY', 'TELCELL', 'IDRAM', 'FASTSHIFT', 'PAYLINK'], label: getTranslation('promocodes.paymentMethods.cashless', language)},
        {value: ['CASH'], label: getTranslation('promocodes.paymentMethods.cash', language)},
    ], [language]);

    const [formData, setFormData] = useState({
        code: '',
        type: mode === 'create' ? 'FIXED' : '',
        value: '',
        upperLimitValue: '',
        validFrom: '',
        validTo: '',
        minimumOrderAmount: 1000,
        maxUses: 1000,
        maxUsesPerUser: 1000,
        userPhoneNumber: '',
        selectedPaymentMethod: mode === 'create' ? 0 : 0, // Index of selected payment option
        allowedPaymentTypes: mode === 'create' ? paymentMethodOptions[0].value : [],
        isActive: true,
        enableCustomerLimit: false,
        enablePaymentLimit: false,
        // Prefill from the query string, e.g. the "Give coupon" link on the Clients page
        ...(mode === 'create' ? prefillFromSearchParams(searchParams) : {})
    });

    const isFetchingPromocode = useRef(false);

    const fetchPromocodeData = useCallback(async () => {
        if (isFetchingPromocode.current) return;
        isFetchingPromocode.current = true;
        try {
            setLoading(true);
            const data = await fetchPromoCodeById(promocodeId);

            // Find which payment method option matches the received allowedPaymentTypes
            let selectedPaymentMethodIndex = 0;
            if (data.allowedPaymentTypes && data.allowedPaymentTypes.length > 0) {
                // Find matching option by comparing arrays
                const paymentMethodOptions = [
                    {value: ['CASH', 'CARD', 'CARD_ON_DELIVERY', 'TELCELL', 'IDRAM', 'FASTSHIFT', 'PAYLINK'], label: getTranslation('promocodes.paymentMethods.any', language)},
                    {value: ['CARD', 'CARD_ON_DELIVERY', 'TELCELL', 'IDRAM', 'FASTSHIFT', 'PAYLINK'], label: getTranslation('promocodes.paymentMethods.cashless', language)},
                    {value: ['CASH'], label: getTranslation('promocodes.paymentMethods.cash', language)}
                ];
                selectedPaymentMethodIndex = paymentMethodOptions.findIndex(option => {
                    if (option.value.length !== data.allowedPaymentTypes.length) return false;
                    return option.value.every(type => data.allowedPaymentTypes.includes(type));
                });
                // Default to 0 if no match found
                if (selectedPaymentMethodIndex === -1) selectedPaymentMethodIndex = 0;
            }

            setFormData({
                code: data.code || '',
                type: data.type || '',
                value: data.value || '',
                upperLimitValue: data.upperLimitValue || '',
                validFrom: data.validFrom || '',
                validTo: data.validTo || '',
                minimumOrderAmount: data.minimumOrderAmount || 1000,
                maxUses: data.maxUses || 10,
                userPhoneNumber: data.userPhoneNumber || '',
                maxUsesPerUser: data.maxUsesPerUser || 1,
                selectedPaymentMethod: selectedPaymentMethodIndex,
                allowedPaymentTypes: data.allowedPaymentTypes || paymentMethodOptions[0].value,
                isActive: data.isActive ?? true,
                enableCustomerLimit: data.maxUsesPerUser > 0,
                enablePaymentLimit: data.allowedPaymentTypes?.length > 0
            });
        } catch (error) {
            console.error('Error fetching promocode:', error);
        } finally {
            setLoading(false);
            isFetchingPromocode.current = false;
        }
    }, [promocodeId, language, paymentMethodOptions]);

    useEffect(() => {
        if (promocodeId && (mode === 'edit' || mode === 'view')) {
            if (!isFetchingPromocode.current) {
            fetchPromocodeData();
            }
        }
    }, [promocodeId, mode, fetchPromocodeData]);

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        
        // Prevent negative values for all number fields
        const numberFields = ['upperLimitValue', 'minimumOrderAmount', 'value', 'maxUses', 'maxUsesPerUser'];
        if (numberFields.includes(name) && value !== '' && Number(value) < 0) {
            return; // Don't update if negative
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = {...prev};
                delete newErrors[name];
                return newErrors;
            });
        }
    };


    const handleToggleChange = (name) => (e) => {
        setFormData(prev => ({
            ...prev,
            [name]: e.target.checked
        }));
    };

    const generateRandomPromoCode = () => {
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        for (let i = 0; i < 7; i++) {
            result += characters.charAt(Math.floor(Math.random() * characters.length));
        }
        setFormData(prev => ({
            ...prev,
            code: result
        }));

        // Clear error if exists
        if (errors.code) {
            setErrors(prev => {
                const newErrors = {...prev};
                delete newErrors.code;
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.code.trim()) {
            newErrors.code = getTranslation('validation.required', language);
        }

        if (!formData.type) {
            newErrors.type = getTranslation('validation.required', language);
        }

        if (!formData.value || formData.value <= 0) {
            newErrors.value = getTranslation('validation.required', language);
        }

        if((formData.type === 'PERCENTAGE' ||  formData.type === 'DELIVERY_PERCENTAGE') && formData.value > 100) {
            newErrors.value = getTranslation('validation.percentageValue', language);
        }

        // Validate upperLimitValue for PERCENTAGE type - required field
        if (formData.type === 'PERCENTAGE') {
            if (!formData.upperLimitValue || formData.upperLimitValue === '') {
                newErrors.upperLimitValue = getTranslation('validation.required', language) || 'This field is required';
            } else if (Number(formData.upperLimitValue) < 0) {
                newErrors.upperLimitValue = getTranslation('validation.negativeValue', language) || 'Value cannot be negative';
            }
        }

        if (!formData.validFrom) {
            newErrors.validFrom = getTranslation('validation.required', language);
        }

        if (formData.userPhoneNumber && !formData.userPhoneNumber.startsWith('+374')) {
            newErrors.userPhoneNumber = getTranslation('validation.invalidPhoneNumber', language);
        }

        if (!formData.validTo) {
            newErrors.validTo = getTranslation('validation.required', language);
        }

        if (formData.validFrom && formData.validTo && new Date(formData.validFrom) >= new Date(formData.validTo)) {
            newErrors.validTo = getTranslation('validation.endDateAfterStartDate', language) || 'End date must be after start date';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isReadOnly) return;

        if (!validateForm()) return;

        try {
            setLoading(true);

            const submitData = {
                code: formData.code,
                type: formData.type,
                value: Number(formData.value),
                validFrom: formData.validFrom,
                validTo: formData.validTo,
                minimumOrderAmount: Number(formData.minimumOrderAmount),
                maxUses: Number(formData.maxUses),
                maxUsesPerUser: formData.enableCustomerLimit ? Number(formData.maxUsesPerUser) : 0,
                userPhoneNumber: formData.userPhoneNumber ? formData.userPhoneNumber : null,
                allowedPaymentTypes: formData.allowedPaymentTypes,
                isActive: formData.isActive,
                sponsor: 'CHEF',
                splitRatio: 0
            };

            // Only include upperLimitValue when type is PERCENTAGE (required field)
            if (formData.type === 'PERCENTAGE') {
                submitData.upperLimitValue = Number(formData.upperLimitValue);
            }

            if (mode === 'create') {
                await createPromoCode(submitData);
                if (toastRef?.current) {
                    toastRef.current.addToast(
                        getTranslation('toast.statusUpdated', language) || "Promocode created successfully",
                        "success"
                    );
                }
            } else if (mode === 'edit') {
                await updatePromoCode(promocodeId, submitData);
                if (toastRef?.current) {
                    toastRef.current.addToast(
                        getTranslation('toast.statusUpdated', language) || "Promocode updated successfully",
                        "success"
                    );
                }
            }

            onSuccess?.();
        } catch (error) {
            console.error('Error saving promocode:', error);
            if (toastRef?.current) {
                toastRef.current.addToast(
                    getTranslation('common.error', language) || "An error occurred",
                    "error"
                );
            }
        } finally {
            setLoading(false);
        }
    };

    const getTitle = () => {
        switch (mode) {
            case 'create':
                return getTranslation('promocodes.createPromocode', language);
            case 'edit':
                return getTranslation('promocodes.editPromocode', language);
            default:
                return getTranslation('promocodes.createPromocode', language);
        }
    };


    if (loading && mode === 'edit') {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-[#161b22] rounded-lg shadow min-h-screen md:min-h-auto border dark:border-[#30363d]">
            {/* Header with back button */}
            <div className="p-4 md:p-6 border-b border-gray-200 dark:border-[#30363d] bg-white dark:bg-[#161b22]">
                <div className="flex items-center mb-2 md:mb-4">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onCancel}
                        className="mr-4 -ml-2"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
                        </svg>
                        <span className="text-sm md:text-base font-medium">{getTitle()}</span>
                    </Button>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-6 md:space-y-6 pb-20 md:pb-6">
                {/* Active Status Toggle */}
                <div className="mb-4 md:mb-6">
                    <div className="flex items-center space-x-3 justify-end">
                        <Toggle
                            id="isActive"
                            checked={formData.isActive}
                            onChange={handleToggleChange('isActive')}
                            disabled={isReadOnly}
                        />
                        <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-[#b1bac4]">
                            {getTranslation('common.active', language) || 'Active'}
                        </label>
                    </div>
                </div>

                {/* Promocode Code + Minimum Order Amount */}
                <div className="space-y-4 md:space-y-6">
                    <div>
                        <label className="text-sm font-bold block text-gray-900 dark:text-[#c9d1d9] mb-2">
                            {getTranslation('promocodes.code', language)}
                            {!isReadOnly && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                        </label>
                        <div className="relative flex">
                            <input
                                name="code"
                                value={formData.code}
                                onChange={handleInputChange}
                                type="text"
                                placeholder="KDS3872!"
                                disabled={isReadOnly}
                                className={`px-4 py-3 md:py-2 w-full placeholder:text-[14px] placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-[#6e7681] text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] ${
                                    errors.code ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-[#30363d]"
                                }`}
                            />
                            {!isReadOnly && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    type="button"
                                    onClick={generateRandomPromoCode}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2"
                                    title="Generate random code"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none"
                                         viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                                    </svg>
                                </Button>
                            )}
                        </div>
                        {errors.code && (
                            <p className="text-red-500 text-xs mt-1">{errors.code}</p>
                        )}
                    </div>

                    <div>
                        <label className="text-sm font-bold block text-gray-900 dark:text-[#c9d1d9] mb-2">
                            {getTranslation('promocodes.minimumOrderAmount', language)} (AMD)
                        </label>
                        <input
                            name="minimumOrderAmount"
                            value={formData.minimumOrderAmount}
                            onChange={handleInputChange}
                            type="number"
                            min="0"
                            placeholder="0 AMD"
                            disabled={isReadOnly}
                            className="px-4 py-3 md:py-2 w-full placeholder:text-[14px] placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-[#6e7681] text-sm border border-gray-300 dark:border-[#30363d] rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                        />
                    </div>
                </div>

                {/* Promocode Type */}
                <div className="mb-4 md:mb-6">
                    <label className="text-sm font-bold block text-gray-900 dark:text-[#c9d1d9] mb-3">
                        {getTranslation('promocodes.discountType', language)}
                        {!isReadOnly && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                    </label>

                    <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-8">
                        {typeOptions.map((option) => (
                            <label key={option.value} className="flex items-center space-x-3 cursor-pointer p-2 -ml-2 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1c2128]">
                                <input
                                    type="radio"
                                    name="type"
                                    value={option.value}
                                    checked={formData.type === option.value}
                                    onChange={handleInputChange}
                                    disabled={isReadOnly}
                                    className="w-5 h-5 md:w-4 md:h-4 text-gray-900 dark:text-[#c9d1d9] border-gray-300 dark:border-[#30363d] focus:ring-gray-900 dark:focus:ring-gray-400 accent-gray-900 dark:accent-gray-400"
                                />
                                <span className="text-sm text-gray-700 dark:text-[#b1bac4] font-medium">{option.label}</span>
                            </label>
                        ))}
                    </div>
                    {errors.type && (
                        <p className="text-red-500 text-xs mt-2">{errors.type}</p>
                    )}
                </div>

                {/* Discount Value - Only show when type is selected */}
                {formData.type && (
                    <div className="mb-4 md:mb-6 space-y-4">
                        <div>
                            <label className="text-sm font-bold block text-gray-900 dark:text-[#c9d1d9] mb-2">
                                {getTranslation('promocodes.discountValue', language) || 'Discount Value'}
                                {!isReadOnly && <span className="text-red-500 dark:text-red-400 ml-1">*</span>}
                            </label>
                        <div className="relative max-w-full md:max-w-[200px]">
                            <input
                                name="value"
                                value={formData.value}
                                onChange={handleInputChange}
                                type="number"
                                    min="0"
                                placeholder="0"
                                disabled={isReadOnly}
                                className={`px-4 py-3 md:py-2 w-full placeholder:text-[14px] placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-[#6e7681] text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] ${
                                    errors.value ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-[#30363d]"
                                } ${formData.type !== 'FIXED' ? 'pr-8' : ''}`}
                            />
                            {formData.type !== 'FIXED' && (
                                <span
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 dark:text-[#8b949e] font-medium">
                                    %
                                </span>
                            )}
                        </div>
                            <p className="text-sm text-gray-600 dark:text-[#8b949e] font-medium mt-2">
                            {formData.type === 'FIXED' ? getTranslation('promocodes.typesDescription.fixed', language) : (formData.type === 'PERCENTAGE' ? getTranslation('promocodes.typesDescription.percentage', language) : getTranslation('promocodes.typesDescription.delivery', language))}
                        </p>
                        {errors.value && (
                            <p className="text-red-500 text-xs mt-1">{errors.value}</p>
                            )}
                        </div>

                        {/* Upper Limit Value - Only show when type is PERCENTAGE */}
                        {formData.type === 'PERCENTAGE' && (
                            <div>
                                <label className="text-sm font-bold block text-gray-900 dark:text-[#c9d1d9] mb-2">
                                    {getTranslation('promocodes.upperLimitValue', language)} (AMD) <span className="text-red-500 dark:text-red-400">*</span>
                                </label>
                                <div className="relative max-w-full md:max-w-[200px]">
                                    <input
                                        name="upperLimitValue"
                                        value={formData.upperLimitValue}
                                        onChange={handleInputChange}
                                        type="number"
                                        min="0"
                                        step="1"
                                        placeholder="0"
                                        disabled={isReadOnly}
                                        className={`px-4 py-3 md:py-2 w-full placeholder:text-[14px] placeholder:font-normal placeholder:text-gray-400 dark:placeholder:text-[#6e7681] text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] ${
                                            errors.upperLimitValue ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-[#30363d]"
                                        }`}
                                    />
                                </div>
                                {errors.upperLimitValue && (
                                    <p className="text-red-500 text-xs mt-1">{errors.upperLimitValue}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Date Range */}
                <div className="flex flex-col md:grid md:grid-cols-2 gap-4 md:gap-6">
                    <SingleDatePicker
                                name="validFrom"
                        value={formData.validFrom}
                                onChange={handleInputChange}
                        label={getTranslation('promocodes.validFrom', language)}
                                disabled={isReadOnly}
                        error={errors.validFrom}
                        placeholder="dd.mm.yyyy"
                        isRequired={true}
                        showRequiredIndicator={!isReadOnly}
                    />
                    <SingleDatePicker
                                name="validTo"
                        value={formData.validTo}
                                onChange={handleInputChange}
                        label={getTranslation('promocodes.validTo', language)}
                                disabled={isReadOnly}
                        error={errors.validTo}
                        placeholder="dd.mm.yyyy"
                        isRequired={true}
                        showRequiredIndicator={!isReadOnly}
                            />
                </div>

                {/* Customer Limit Toggle */}
                <div className="flex items-center space-x-3 p-2 -ml-2 rounded-lg">
                    <Toggle
                        id="enableCustomerLimit"
                        checked={formData.enableCustomerLimit}
                        onChange={handleToggleChange('enableCustomerLimit')}
                        disabled={isReadOnly}
                    />
                    <label htmlFor="enableCustomerLimit" className="text-sm font-medium text-gray-700 dark:text-[#b1bac4]">
                        {getTranslation('promocodes.enableCustomerLimit', language)}
                    </label>
                </div>

                {/* Customer Limit Fields */}
                {formData.enableCustomerLimit && (
                    <div className="ml-4 md:ml-8 space-y-4">
                        <div className="text-sm text-gray-600 dark:text-[#8b949e] font-medium">
                            {getTranslation('promocodes.customerLimitDescription', language)}
                        </div>
                        <div className="flex flex-col md:grid md:grid-cols-2 gap-4 md:gap-6">
                            <div>
                                <label className="text-sm font-medium block text-gray-700 dark:text-[#b1bac4] mb-2">
                                    {getTranslation('promocodes.maxUses', language)}
                                </label>
                                <input
                                    name="maxUses"
                                    value={formData.maxUses}
                                    onChange={handleInputChange}
                                    type="number"
                                    min="0"
                                    disabled={isReadOnly}
                                    className="px-4 py-3 md:py-2 w-full text-sm border border-gray-300 dark:border-[#30363d] rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                />
                            </div>

                            <div>
                                <label className="text-sm font-medium block text-gray-700 dark:text-[#b1bac4] mb-2">
                                    {getTranslation('promocodes.maxUsesPerUser', language)}
                                </label>
                                <input
                                    name="maxUsesPerUser"
                                    value={formData.maxUsesPerUser}
                                    onChange={handleInputChange}
                                    type="number"
                                    min="0"
                                    disabled={isReadOnly}
                                    className="px-4 py-3 md:py-2 w-full text-sm border border-gray-300 dark:border-[#30363d] rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9]"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium block text-gray-700 dark:text-[#b1bac4] mb-2">
                                {getTranslation('promocodes.userType', language) || 'User Type'}
                            </label>
                            <div className="relative">
                                <input
                                    name="userPhoneNumber"
                                    value={formData.userPhoneNumber}
                                    onChange={handleInputChange}
                                    type="text"
                                    maxLength={12}
                                    placeholder={getTranslation('promocodes.userTypePlaceholder', language) || 'Enter user type (optional)'}
                                    disabled={isReadOnly}
                                    className={`px-4 py-3 md:py-2 w-full text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#c9d1d9] placeholder:text-gray-400 dark:placeholder:text-[#6e7681] ${
                                        errors.userPhoneNumber ? "border-red-500 dark:border-red-400" : "border-gray-300 dark:border-[#30363d]"
                                    }`}
                                />
                            </div>
                            {errors.userPhoneNumber && (
                                <p className="text-red-500 text-xs mt-1">{errors.userPhoneNumber}</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Allowed payment types */}
                <div>
                    <label className="text-sm font-bold block text-gray-900 dark:text-[#c9d1d9] mb-3">
                        {getTranslation('promocodes.enablePaymentLimit', language) || 'Payment Method'}
                    </label>
                    <div className="flex flex-col md:flex-row md:items-center space-y-3 md:space-y-0 md:space-x-8">
                        {paymentMethodOptions.map((option, index) => (
                            <label key={index} className="flex items-center space-x-3 cursor-pointer p-2 -ml-2 rounded-lg hover:bg-gray-50 dark:hover:bg-[#1c2128] transition-colors">
                                <input
                                    type="radio"
                                    name="paymentMethod"
                                    value={index}
                                    checked={formData.selectedPaymentMethod === index}
                                    onChange={(e) => {
                                        const selectedIndex = parseInt(e.target.value);
                                        setFormData(prev => ({
                                            ...prev,
                                            selectedPaymentMethod: selectedIndex,
                                            allowedPaymentTypes: paymentMethodOptions[selectedIndex].value
                                        }));
                                    }}
                                    disabled={isReadOnly}
                                    className="w-5 h-5 md:w-4 md:h-4 text-gray-900 dark:text-[#c9d1d9] border-gray-300 dark:border-[#30363d] focus:ring-gray-900 dark:focus:ring-gray-400 accent-gray-900 dark:accent-gray-400"
                                />
                                <span className="text-sm text-gray-700 dark:text-[#b1bac4] font-medium">{option.label}</span>
                            </label>
                        ))}
                    </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 md:pt-6">
                    {!isReadOnly && (
                        <Button
                            variant="primary"
                            size="lg"
                            type="submit"
                            disabled={loading}
                            loading={loading}
                            className="w-full md:w-auto text-base md:text-sm"
                        >
                            {!loading && (mode === 'create'
                                    ? getTranslation('promocodes.addPromocode', language)
                                : getTranslation('common.update', language))}
                            {loading && getTranslation('common.saving', language)}
                        </Button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default PromocodeForm; 
